import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react'
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { Link, useSearchParams } from 'react-router-dom'
import {
  getDistributionHistory,
  getObligations,
  logDistribution,
  logStandaloneCatchUp,
  proposeDistribution,
} from '@/services/waterfall'
import type {
  MemberAmounts,
  ObligationsResponse,
  ProposalResponse,
  ProposalVestingItem,
  TierBreakdown,
} from '@/types/waterfall'
import { MEMBERS, MODE_HELP, MODE_LABELS, TIER_LABELS } from '@/types/waterfall'
import { formatApiError } from '@/utils/apiError'
import {
  clearCalculatorCache,
  loadCalculatorCache,
  saveCalculatorCache,
} from '@/utils/calculatorCache'
import {
  formatAmountForClipboard,
  distributionAmountFromQuery,
  formatCurrencyField,
  formatDisplayDate,
  inputsMatchSnapshot,
  inputsToApiMode,
  parseDistributionAmount,
  todayIsoDate,
  type CalculatorInputs,
} from '@/utils/waterfallCalculator'
import {
  allocateMemberTotals,
  cloneTierBreakdown,
  gridCashTotal,
  gridGrandTotal,
  gridHasNegative,
  memberPaymentDelta,
  memberTotalsFromGrid,
  memberWithheld,
  parseCellInput,
  R2,
  roundTierBreakdown,
  tierColumnTotal,
  tierKeys,
} from '@/utils/waterfallGrid'
import { toast } from '@/store/toastStore'
import { HoverHelp } from '@/tableControls'
import { useMaxMd } from '@/hooks/useMaxMd'
import { usePageAccess } from '@/hooks/usePageAccess'
import {
  computeSplitTableWidths,
  resolveSplitTableDisplayWidths,
  splitTableMinWidth,
  SPLIT_TABLE_BUILDUP_LABELS,
  SPLIT_TABLE_LABEL_COLUMN_SCALE,
  SPLIT_TABLE_MOBILE_LABEL_COLUMN_SCALE,
  type SplitTableWidths,
} from '@/utils/splitTableLayout'
import WaterfallFlow from './WaterfallFlow'
import WaterfallSankey from './WaterfallSankey'
import WaterfallTabHeader from './WaterfallTabHeader'
import { TierBuildupDetailRows, TierBuildupRemainingRow } from './TierBuildupRows'
import VestingCatchUpPanel from './VestingCatchUpPanel'
import {
  buildCatchUpLogFields,
  loadVestingCatchUpThreshold,
  prefillCatchUpAmount,
  splitCatchUp,
} from '@/utils/vestingCatchup'
import AllocationCalculator from './AllocationCalculator'
import DistributionModeToggle from './DistributionModeToggle'
import { useDistributionModeStore } from '@/store/distributionModeStore'

function emptyMemberAmounts(): MemberAmounts {
  return { JR: 0, KB: 0, LVW: 0, TR: 0, SM: 0 }
}

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const SPLIT_LABEL_HEAD =
  'sticky left-0 z-20 bg-gray-50 border-r border-gray-200 px-3 py-2 max-md:px-2 text-left font-medium text-gray-600 whitespace-nowrap'
const SPLIT_LABEL_CELL =
  'sticky left-0 z-10 bg-white group-hover:bg-gray-50 border-r border-gray-200 px-3 py-2 max-md:px-2 text-left whitespace-nowrap'
const SPLIT_NUM_HEAD =
  'px-3 py-2 max-md:px-2 text-right font-medium text-gray-600 whitespace-nowrap'
const SPLIT_NUM_CELL = 'px-3 py-2 max-md:px-2 text-right tabular-nums whitespace-nowrap'
const SPLIT_TOTAL_HEAD = `${SPLIT_NUM_HEAD} border-l border-gray-300`
const SPLIT_TOTAL_CELL = `${SPLIT_NUM_CELL} border-l border-gray-300 font-medium`

const TOTALS_ROW_BG = 'bg-blue-50'
const TOTALS_ROW_BORDER = 'border-t-2 border-t-blue-200'
const SPLIT_TOTALS_LABEL_CELL = `sticky left-0 z-10 ${TOTALS_ROW_BG} border-r border-gray-200 px-3 py-2 max-md:px-2 text-left whitespace-nowrap ${TOTALS_ROW_BORDER} font-bold text-gray-900`
const SPLIT_TOTALS_NUM_CELL = `${SPLIT_NUM_CELL} ${TOTALS_ROW_BG} ${TOTALS_ROW_BORDER} font-bold text-gray-900`
const SPLIT_TOTALS_TOTAL_CELL = `${SPLIT_NUM_CELL} border-l border-gray-300 ${TOTALS_ROW_BG} ${TOTALS_ROW_BORDER} font-bold text-gray-900`

function SplitTableColgroup({ widths }: { widths: SplitTableWidths }) {
  return (
    <colgroup>
      <col style={{ width: widths.labelPx }} />
      {MEMBERS.map((m) => (
        <col key={m} style={{ width: widths.valuePx }} />
      ))}
      <col style={{ width: widths.valuePx }} />
    </colgroup>
  )
}

interface CalculationSnapshot {
  inputs: CalculatorInputs
  parsedAmount: number
  expectedLastNum: number
  result: ProposalResponse
  proposalGrid: TierBreakdown
  vesting: ProposalVestingItem[]
}

function CalculatorCash() {
  const maxMd = useMaxMd()
  const { canEdit } = usePageAccess('distributions')
  const [searchParams] = useSearchParams()
  const cached = loadCalculatorCache()
  const [distDate, setDistDate] = useState(cached?.snapshot.inputs.distDate ?? todayIsoDate())
  const [amount, setAmount] = useState(cached?.snapshot.inputs.amount ?? '')
  const [applyTrueUps, setApplyTrueUps] = useState(cached?.snapshot.inputs.applyTrueUps ?? true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [snapshot, setSnapshot] = useState<CalculationSnapshot | null>(
    cached?.snapshot
      ? {
          ...cached.snapshot,
          vesting: cached.snapshot.vesting ?? [],
        }
      : null,
  )
  const [actualTotals, setActualTotals] = useState<MemberAmounts | null>(
    cached?.actualTotals ?? null,
  )
  const [obligations, setObligations] = useState<ObligationsResponse | null>(null)
  const [proFormaDistributionNum, setProFormaDistributionNum] = useState<number | null>(null)
  const [amountFocused, setAmountFocused] = useState(false)
  const [amountDraft, setAmountDraft] = useState('')
  const [editingMember, setEditingMember] = useState<(typeof MEMBERS)[number] | null>(null)
  const [memberDraft, setMemberDraft] = useState('')
  const [viewMode, setViewMode] = useState<'table' | 'flow'>(cached?.viewMode ?? 'table')
  const [expandedTiers, setExpandedTiers] = useState<Set<keyof TierBreakdown>>(new Set())
  const [confirmLogOpen, setConfirmLogOpen] = useState(false)
  const [logNote, setLogNote] = useState('')
  const [logging, setLogging] = useState(false)
  const [logSuccess, setLogSuccess] = useState<string | null>(null)
  const [catchUpAmounts, setCatchUpAmounts] = useState<MemberAmounts>(emptyMemberAmounts())
  const [catchUpSeparate, setCatchUpSeparate] = useState(false)
  const [editingCatchUpMember, setEditingCatchUpMember] = useState<(typeof MEMBERS)[number] | null>(
    null,
  )
  const [catchUpMemberDraft, setCatchUpMemberDraft] = useState('')
  const splitTablesWrapRef = useRef<HTMLDivElement>(null)
  const [splitTablesContainerWidth, setSplitTablesContainerWidth] = useState(0)
  const requestSeq = useRef(0)

  useEffect(() => {
    const fromQuery = distributionAmountFromQuery(
      searchParams,
      cached?.snapshot.inputs.amount,
    )
    if (fromQuery) {
      setAmount(fromQuery)
    }
  }, [searchParams, cached?.snapshot.inputs.amount])

  const currentInputs: CalculatorInputs = { distDate, amount, applyTrueUps }
  const resultStale =
    snapshot !== null && !inputsMatchSnapshot(currentInputs, snapshot.inputs)

  useEffect(() => {
    if (!cached?.snapshot || resultStale) return
    void (async () => {
      try {
        const ob = await getObligations(cached.snapshot.inputs.distDate)
        setObligations(ob)
      } catch {
        /* side panel is optional when restoring cache */
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (resultStale) {
      setSnapshot(null)
      setActualTotals(null)
      setExpandedTiers(new Set())
      setCatchUpAmounts(emptyMemberAmounts())
      setCatchUpSeparate(false)
    }
  }, [resultStale])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const ob = await getObligations(distDate)
        if (!cancelled) {
          setObligations(ob)
          if (ob.next_distribution_num != null) {
            setProFormaDistributionNum(ob.next_distribution_num)
          }
        }
      } catch {
        if (!cancelled) setObligations(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [distDate])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const data = await getDistributionHistory()
        if (cancelled) return
        const last = data.distributions.at(-1)
        setProFormaDistributionNum(last ? last.num + 1 : 1)
      } catch {
        if (!cancelled) setProFormaDistributionNum(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [logSuccess])

  useEffect(() => {
    if (snapshot && actualTotals && !resultStale) {
      saveCalculatorCache({ snapshot, actualTotals, viewMode })
    }
  }, [snapshot, actualTotals, viewMode, resultStale])

  const handleReset = useCallback(() => {
    requestSeq.current += 1
    clearCalculatorCache()
    setDistDate(todayIsoDate())
    setAmount('')
    setApplyTrueUps(true)
    setAmountFocused(false)
    setAmountDraft('')
    setLoading(false)
    setError(null)
    setSnapshot(null)
    setActualTotals(null)
    setEditingMember(null)
    setMemberDraft('')
    setViewMode('table')
    setExpandedTiers(new Set())
    setConfirmLogOpen(false)
    setLogNote('')
    setLogSuccess(null)
    setCatchUpAmounts(emptyMemberAmounts())
    setCatchUpSeparate(false)
    setEditingCatchUpMember(null)
    setCatchUpMemberDraft('')
  }, [])

  const runProposal = useCallback(async () => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(null)
    setLogSuccess(null)

    const parsed = parseDistributionAmount(amount)
    if (parsed === null) {
      setError('Enter a valid distribution amount (up to two decimal places).')
      setLoading(false)
      return
    }

    const inputsAtSubmit: CalculatorInputs = { distDate, amount, applyTrueUps }
    const mode = inputsToApiMode(applyTrueUps)

    try {
      const [proposal, ob] = await Promise.all([
        proposeDistribution({
          date: distDate,
          amount: parsed,
          mode,
        }),
        getObligations(distDate),
      ])

      if (seq !== requestSeq.current) return

      const proposalGrid = cloneTierBreakdown(proposal.proposal.row_for_sheet)
      const threshold = loadVestingCatchUpThreshold()
      const prefilled = emptyMemberAmounts()
      for (const item of proposal.vesting ?? []) {
        if (MEMBERS.includes(item.member as (typeof MEMBERS)[number])) {
          prefilled[item.member as (typeof MEMBERS)[number]] = prefillCatchUpAmount(
            item.pro_forma_payable,
            threshold,
          )
        }
      }
      const nextSnapshot: CalculationSnapshot = {
        inputs: inputsAtSubmit,
        parsedAmount: parsed,
        expectedLastNum: Number(proposal.inputs.num) - 1,
        result: proposal,
        proposalGrid,
        vesting: proposal.vesting ?? [],
      }
      setSnapshot(nextSnapshot)
      setCatchUpAmounts(prefilled)
      setCatchUpSeparate(false)
      setActualTotals({ ...proposal.proposal.by_member })
      setExpandedTiers(new Set())
      setObligations(ob)
    } catch (e) {
      if (seq !== requestSeq.current) return
      setError(formatApiError(e, 'Failed to compute proposal'))
      setSnapshot(null)
      setActualTotals(null)
      setObligations(null)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [amount, applyTrueUps, distDate])

  const distributionCatchUpTotal = catchUpSeparate
    ? 0
    : MEMBERS.reduce((sum, m) => sum + catchUpAmounts[m], 0)
  const standaloneCatchUpTotal = catchUpSeparate
    ? MEMBERS.reduce((sum, m) => sum + catchUpAmounts[m], 0)
    : 0
  const hasStandaloneCatchUpRow = catchUpSeparate && standaloneCatchUpTotal > 0.005

  const smVestingItem = snapshot?.vesting.find((v) => v.member === 'SM')
  const proposalWithheld = snapshot?.result.proposal.withheld
  const smVN =
    smVestingItem?.this_distribution ??
    (proposalWithheld ? memberWithheld(proposalWithheld, 'SM') : 0)
  const smCatchUpSplit = splitCatchUp(catchUpAmounts.SM, smVN)

  const lockedMembers = useMemo(() => {
    if (!proposalWithheld) return new Set<(typeof MEMBERS)[number]>()
    return new Set(MEMBERS.filter((m) => memberWithheld(proposalWithheld, m) > 0.005))
  }, [proposalWithheld])

  const displayActualAmount = useCallback(
    (member: (typeof MEMBERS)[number]) => {
      if (!actualTotals) return 0
      if (!catchUpSeparate && lockedMembers.has(member) && member === 'SM') {
        return R2(smCatchUpSplit.c1 + smCatchUpSplit.c2)
      }
      return catchUpSeparate
        ? actualTotals[member]
        : actualTotals[member] + catchUpAmounts[member]
    },
    [
      actualTotals,
      catchUpAmounts,
      catchUpSeparate,
      lockedMembers,
      smCatchUpSplit.c1,
      smCatchUpSplit.c2,
    ],
  )

  const copyActualPayment = useCallback(
    async (member: (typeof MEMBERS)[number]) => {
      const amount =
        editingMember === member
          ? (parseDistributionAmount(memberDraft) ?? displayActualAmount(member))
          : displayActualAmount(member)
      try {
        await navigator.clipboard.writeText(formatAmountForClipboard(amount))
      } catch (err) {
        console.error('Failed to copy actual payment:', err)
      }
    },
    [displayActualAmount, editingMember, memberDraft],
  )

  const copyMinimumTotal = useCallback(async () => {
    if (!obligations) return
    try {
      await navigator.clipboard.writeText(formatAmountForClipboard(obligations.minimum_to_clear))
      toast.success('Copied')
    } catch {
      toast.error('Failed to copy')
    }
  }, [obligations])

  const handleCatchUpChange = useCallback((amounts: MemberAmounts) => {
    setCatchUpAmounts(amounts)
    if (MEMBERS.every((m) => amounts[m] <= 0.005)) {
      setCatchUpSeparate(false)
    }
  }, [])

  const updateActualTotal = (member: (typeof MEMBERS)[number], raw: string) => {
    if (!actualTotals || lockedMembers.has(member)) return
    const parsed = parseCellInput(raw)
    if (parsed === null) return
    const ordinary = catchUpSeparate ? parsed : parsed - catchUpAmounts[member]
    setActualTotals({ ...actualTotals, [member]: ordinary })
  }

  const updateStandaloneCatchUp = (member: (typeof MEMBERS)[number], raw: string) => {
    const item = snapshot?.vesting.find((v) => v.member === member)
    const parsed = parseCellInput(raw)
    if (parsed === null) return
    const clamped = Math.min(Math.max(parsed, 0), item?.pro_forma_payable ?? parsed)
    setCatchUpAmounts({ ...catchUpAmounts, [member]: clamped })
  }

  const handleLogDistribution = async () => {
    if (!canEdit || !snapshot || !actualTotals || !proposalWithheld) return
    const logGrid = roundTierBreakdown(
      allocateMemberTotals(snapshot.proposalGrid, actualTotals, lockedMembers),
    )
    if (gridHasNegative(logGrid)) {
      setError('Cannot log a distribution with negative amounts.')
      setConfirmLogOpen(false)
      return
    }
    const separateMembers = catchUpSeparate
      ? MEMBERS.filter((m) => catchUpAmounts[m] > 0.005)
      : []
    const C = catchUpAmounts.SM
    const logFields = buildCatchUpLogFields({
      mode: catchUpSeparate ? 'separate' : 'this',
      proposalWithheld,
      C,
      vN: smVN,
    })
    setLogging(true)
    setError(null)
    try {
      const baseTotal = R2(gridGrandTotal(logGrid))
      const logged = await logDistribution({
        date: snapshot.inputs.distDate,
        amount: baseTotal,
        expected_last_num: snapshot.expectedLastNum,
        note: logNote.trim() || null,
        withheld: logFields.withheld,
        catch_up_addons: logFields.catch_up_addons,
        ...logGrid,
      })

      let lastNum = logged.num
      const catchUpDistNums: number[] = []
      for (const member of separateMembers) {
        const catchUpLogged = await logStandaloneCatchUp({
          date: snapshot.inputs.distDate,
          member: member as 'SM',
          amount: catchUpAmounts[member],
          expected_last_num: lastNum,
          note: null,
        })
        catchUpDistNums.push(catchUpLogged.num)
        lastNum = catchUpLogged.num
      }

      const clauses: string[] = []
      if (logged.total > 0.005) clauses.push(`${fmt.format(logged.total)} gross`)
      if (logged.cash_total > 0.005) {
        clauses.push(`${fmt.format(logged.cash_total)} paid to members`)
      }
      if (logged.withheld_total > 0.005) {
        clauses.push(`${fmt.format(logged.withheld_total)} accrued to Scott's vesting payable`)
      }
      if (C > 0.005) clauses.push(`${fmt.format(C)} vesting catch-up paid`)
      const summary =
        clauses.length > 0
          ? clauses.join(', ')
          : fmt.format(logged.total)
      if (catchUpDistNums.length > 0) {
        setLogSuccess(
          `Distribution #${logged.num} logged for ${logged.date}: ${summary}. ` +
            `Separate catch-up logged as distribution ${catchUpDistNums.map((n) => `#${n}`).join(', ')}.`,
        )
      } else {
        setLogSuccess(`Distribution #${logged.num} logged for ${logged.date}: ${summary}.`)
      }
      clearCalculatorCache()
      setSnapshot(null)
      setActualTotals(null)
      setObligations(null)
      setLogNote('')
      setConfirmLogOpen(false)
      setCatchUpAmounts(emptyMemberAmounts())
      setCatchUpSeparate(false)
    } catch (e) {
      setError(formatApiError(e, 'Failed to log distribution'))
      setConfirmLogOpen(false)
    } finally {
      setLogging(false)
    }
  }

  const result = snapshot?.result ?? null
  const proposalGrid = snapshot?.proposalGrid ?? null
  const logGrid =
    snapshot && actualTotals
      ? allocateMemberTotals(snapshot.proposalGrid, actualTotals, lockedMembers)
      : null
  const pendingLogFields =
    proposalWithheld && logGrid
      ? buildCatchUpLogFields({
          mode: catchUpSeparate ? 'separate' : 'this',
          proposalWithheld,
          C: catchUpAmounts.SM,
          vN: smVN,
        })
      : null
  const grossPaymentTotal =
    logGrid !== null
      ? R2(
          gridGrandTotal(logGrid) +
            (!catchUpSeparate && smCatchUpSplit.c2 > 0.005 ? smCatchUpSplit.c2 : 0),
        )
      : 0
  const cashPaymentTotal =
    logGrid && pendingLogFields
      ? gridCashTotal(logGrid, pendingLogFields.withheld)
      : 0
  const canLog =
    canEdit &&
    snapshot !== null &&
    actualTotals !== null &&
    logGrid !== null &&
    !resultStale &&
    !loading &&
    !gridHasNegative(logGrid)

  const modeKey = applyTrueUps ? 'sheet' : 'no_trueups'
  const paymentDeltas =
    snapshot && actualTotals
      ? memberPaymentDelta(snapshot.result.proposal.by_member, actualTotals)
      : null

  const expandableTierKeys = useMemo(() => {
    if (!proposalGrid) return [] as (keyof TierBreakdown)[]
    return tierKeys().filter((tierKey) => {
      const total = tierColumnTotal(proposalGrid[tierKey])
      return !(total === 0 && tierKey.startsWith('s4'))
    })
  }, [proposalGrid])

  const allTiersExpanded =
    expandableTierKeys.length > 0 &&
    expandableTierKeys.every((tierKey) => expandedTiers.has(tierKey))

  const toggleAllTierBuildups = () => {
    if (allTiersExpanded) {
      setExpandedTiers(new Set())
      return
    }
    setExpandedTiers(new Set(expandableTierKeys))
  }

  const splitTableWidths = useMemo(() => {
    if (!snapshot || !proposalGrid || !actualTotals) return null

    const labelTexts = [
      'Tier',
      'Calculated total',
      'Actual payment',
      'Difference',
      ...SPLIT_TABLE_BUILDUP_LABELS,
    ]
    const memberTexts = Object.fromEntries(MEMBERS.map((m) => [m, [m]])) as Record<
      (typeof MEMBERS)[number],
      string[]
    >
    const memberInputTexts = Object.fromEntries(MEMBERS.map((m) => [m, [] as string[]])) as Record<
      (typeof MEMBERS)[number],
      string[]
    >
    const totalTexts = ['Total']

    for (const tierKey of tierKeys()) {
      const tier = proposalGrid[tierKey]
      const total = tierColumnTotal(tier)
      if (total === 0 && tierKey.startsWith('s4')) continue
      labelTexts.push(TIER_LABELS[tierKey])
      for (const member of MEMBERS) {
        memberTexts[member].push(tier[member] === 0 ? '—' : fmt.format(tier[member]))
      }
      totalTexts.push(fmt.format(total))
    }

    const actualGrandTotal = MEMBERS.reduce((sum, member) => sum + actualTotals[member], 0)
    const totalDelta = actualGrandTotal - snapshot.result.proposal.total

    for (const member of MEMBERS) {
      const delta = paymentDeltas?.[member] ?? 0
      const displayed = catchUpSeparate
        ? actualTotals[member]
        : actualTotals[member] + catchUpAmounts[member]
      memberTexts[member].push(fmt.format(snapshot.result.proposal.by_member[member]))
      memberInputTexts[member].push(formatCurrencyField(displayed) || '$0.00')
      memberTexts[member].push(
        Math.abs(delta) > 0.005
          ? delta < 0
            ? `${fmt.format(Math.abs(delta))} owed`
            : `${fmt.format(delta)} over`
          : '—',
      )
    }

    totalTexts.push(fmt.format(snapshot.result.proposal.total))
    totalTexts.push(
      fmt.format(
        MEMBERS.reduce(
          (sum, member) =>
            sum +
            (catchUpSeparate
              ? actualTotals[member]
              : actualTotals[member] + catchUpAmounts[member]),
          0,
        ),
      ),
    )
    totalTexts.push(Math.abs(totalDelta) > 0.005 ? fmt.format(totalDelta) : '—')

    const labelScale = maxMd
      ? SPLIT_TABLE_MOBILE_LABEL_COLUMN_SCALE
      : SPLIT_TABLE_LABEL_COLUMN_SCALE
    const widths = computeSplitTableWidths({
      labelTexts,
      memberTexts,
      totalTexts,
      memberInputTexts,
      compact: maxMd,
    })
    return {
      ...widths,
      labelPx: Math.ceil(widths.labelPx * labelScale),
    }
  }, [snapshot, proposalGrid, actualTotals, paymentDeltas, catchUpAmounts, catchUpSeparate, maxMd])

  useEffect(() => {
    const el = splitTablesWrapRef.current
    if (!el) return

    const updateWidth = () => {
      setSplitTablesContainerWidth(el.clientWidth)
    }

    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(el)
    return () => observer.disconnect()
  }, [snapshot, proposalGrid, actualTotals])

  const splitTableDisplayWidths = useMemo(() => {
    if (!splitTableWidths) return null
    const contentWidth = Math.max(splitTablesContainerWidth - 32, 0)
    return resolveSplitTableDisplayWidths(splitTableWidths, contentWidth)
  }, [splitTableWidths, splitTablesContainerWidth])

  const splitTableMinW = splitTableDisplayWidths
    ? splitTableMinWidth(splitTableDisplayWidths)
    : undefined

  return (
    <div className="w-full">
      <WaterfallTabHeader
        title="Cash distribution calculator"
        description="Create a new distribution split, adjust who gets paid today, and log the result in BookKeeper history."
      />

      <div className="space-y-6">
        <section
          data-readonly-allow
          className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 shadow-sm"
        >
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">Create new distribution</h3>
              <p className="mt-0.5 text-xs text-gray-500">
                Enter the ACH initiation date and amount, choose whether to apply true-ups, then
                calculate the split.
              </p>
            </div>
            <form
              onSubmit={(event) => {
                event.preventDefault()
                void runProposal()
              }}
            >
              <div
                className={
                  maxMd
                    ? 'flex flex-col gap-4'
                    : 'flex flex-wrap items-start gap-4'
                }
              >
                <label className={maxMd ? 'block w-full' : 'block flex-1 basis-40'}>
                  <span className="text-sm font-medium text-gray-700">ACH initiation date</span>
                  <input
                    type="date"
                    value={distDate}
                    onChange={(e) => setDistDate(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  />
                  {proFormaDistributionNum != null && (
                    <p className="mt-1 text-xs text-gray-400">
                      {`Pro forma distribution #${proFormaDistributionNum}`}
                    </p>
                  )}
                </label>
                <label className={maxMd ? 'block w-full' : 'block flex-1 basis-48'}>
                  <span className="text-sm font-medium text-gray-700">
                    {obligations ? (
                      <HoverHelp
                        label="Distribution amount"
                        aria-label="Distribution amount help"
                      >
                        <p className="font-semibold text-gray-900">Distribution sizing</p>
                        <p className="mt-0.5 text-gray-500">
                          Payment date {formatDisplayDate(distDate)}
                        </p>

                        <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Accruals tab (through #{obligations.last_distribution_num})
                        </p>
                        <dl className="mt-1 space-y-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <dt className="text-gray-600">Net outstanding</dt>
                            <dd className="font-medium tabular-nums text-gray-900">
                              {fmt.format(obligations.accruals_net_outstanding)}
                            </dd>
                          </div>
                          <div className="flex items-baseline justify-between gap-3 pl-2">
                            <dt className="text-gray-500">Tier 1 + 2</dt>
                            <dd className="tabular-nums text-gray-700">
                              {fmt.format(obligations.accruals_t1_hp_outstanding)}
                            </dd>
                          </div>
                          {Math.abs(obligations.accruals_t3_net) > 0.005 && (
                            <div className="flex items-baseline justify-between gap-3 pl-2">
                              <dt className="text-gray-500">Tier 3 net</dt>
                              <dd className="tabular-nums text-gray-700">
                                {fmt.format(obligations.accruals_t3_net)}
                              </dd>
                            </div>
                          )}
                        </dl>

                        <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          New accrual since {formatDisplayDate(obligations.last_distribution_date)}
                        </p>
                        <dl className="mt-1 space-y-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <dt className="text-gray-600">Tier 1</dt>
                            <dd className="tabular-nums text-gray-900">
                              {fmt.format(obligations.stub_t1_through_asof)}
                            </dd>
                          </div>
                          <div className="flex items-baseline justify-between gap-3">
                            <dt className="text-gray-600">Tier 2 (HP)</dt>
                            <dd className="tabular-nums text-gray-900">
                              {fmt.format(obligations.stub_hp_through_asof)}
                            </dd>
                          </div>
                          <div className="flex items-baseline justify-between gap-3 border-t border-gray-100 pt-1">
                            <dt className="text-gray-600">Projected accruals</dt>
                            <dd className="font-medium tabular-nums text-gray-900">
                              {fmt.format(obligations.projected_accruals_net)}
                            </dd>
                          </div>
                        </dl>

                        <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Min. distribution — clear Tier 1 &amp; 2
                        </p>
                        <dl className="mt-1 space-y-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <dt className="text-gray-600">Tier 1 owed</dt>
                            <dd className="tabular-nums text-gray-900">
                              {fmt.format(obligations.t1_owed)}
                            </dd>
                          </div>
                          <div className="flex items-baseline justify-between gap-3">
                            <dt className="text-gray-600">Tier 2 (HP) owed</dt>
                            <dd className="tabular-nums text-gray-900">
                              {fmt.format(obligations.hp_owed)}
                            </dd>
                          </div>
                          <div className="flex items-baseline justify-between gap-3 border-t border-gray-100 pt-1">
                            <dt className="font-medium text-gray-800">Minimum total</dt>
                            <dd>
                              <button
                                type="button"
                                onClick={() => void copyMinimumTotal()}
                                onMouseDown={(e) => e.preventDefault()}
                                className="cursor-pointer border-0 bg-transparent p-0 font-semibold tabular-nums text-gray-900 hover:text-brand-700 hover:underline"
                                title="Copy amount"
                                aria-label="Copy minimum total"
                              >
                                {fmt.format(obligations.minimum_to_clear)}
                              </button>
                            </dd>
                          </div>
                        </dl>

                        <p className="mt-2.5 text-[11px] leading-relaxed text-gray-500">
                          Tier 3 and Scott vesting are not included in the minimum above. See the
                          Accruals and Vesting tabs for those balances.
                        </p>
                      </HoverHelp>
                    ) : (
                      'Distribution amount'
                    )}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={
                      amountFocused
                        ? amountDraft
                        : amount
                          ? formatCurrencyField(parseDistributionAmount(amount) ?? 0)
                          : ''
                    }
                    onFocus={() => {
                      setAmountFocused(true)
                      setAmountDraft(amount)
                    }}
                    onChange={(e) => {
                      setAmountDraft(e.target.value)
                      setAmount(e.target.value)
                    }}
                    onBlur={() => {
                      const parsed = parseDistributionAmount(amountDraft || amount)
                      if (parsed !== null) {
                        setAmount(formatCurrencyField(parsed))
                      }
                      setAmountFocused(false)
                    }}
                    placeholder="$0.00"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm tabular-nums shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  />
                </label>
                <div className={maxMd ? 'block w-full' : 'block flex-1 basis-48'}>
                  <span className="text-sm font-medium text-gray-700">
                    <HoverHelp label="Apply true-ups" aria-label="Apply true-ups help">
                      <p className="font-medium">{MODE_LABELS[modeKey]}</p>
                      <p className="mt-1">{MODE_HELP[modeKey]}</p>
                    </HoverHelp>
                  </span>
                  <div
                    role="group"
                    aria-label="Apply true-ups"
                    className="mt-1 inline-flex w-full rounded-md border border-gray-300 bg-gray-50 p-0.5"
                  >
                    <button
                      type="button"
                      aria-pressed={applyTrueUps}
                      onClick={() => setApplyTrueUps(true)}
                      className={`flex-1 rounded-[5px] px-3 py-1.5 text-sm font-medium transition-colors ${
                        applyTrueUps
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      On
                    </button>
                    <button
                      type="button"
                      aria-pressed={!applyTrueUps}
                      onClick={() => setApplyTrueUps(false)}
                      className={`flex-1 rounded-[5px] px-3 py-1.5 text-sm font-medium transition-colors ${
                        !applyTrueUps
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Off
                    </button>
                  </div>
                </div>
                <div
                  className={
                    maxMd
                      ? 'flex w-full flex-col gap-2'
                      : 'flex flex-1 basis-56 items-start gap-2 self-start pt-6'
                  }
                >
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex min-h-[2.75rem] w-full flex-1 items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm disabled:opacity-50 btn-primary max-md:min-h-11"
                    style={{ background: 'var(--gradient-primary)' }}
                  >
                    {loading ? 'Calculating…' : 'Calculate split'}
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    disabled={loading}
                    className="inline-flex min-h-[2.75rem] w-full flex-1 items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-normal text-gray-500 hover:bg-gray-50 hover:text-gray-600 disabled:opacity-50 max-md:min-h-11"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </form>
            {error && (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            {logSuccess && (
              <div className="mt-3 text-sm text-green-700" role="status">
                <p>{logSuccess}</p>
                <Link
                  to="/distribution-waterfall/history"
                  className="mt-1 inline-block font-medium text-brand-700 hover:underline"
                >
                  View distribution history →
                </Link>
              </div>
            )}
          </section>

          {resultStale && (
            <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Inputs changed since the last calculation. Recalculate before logging.
            </section>
          )}

          {result && proposalGrid && snapshot && actualTotals && (
            <div ref={splitTablesWrapRef} className="space-y-6">
              <div data-readonly-allow className="space-y-6">
              <section className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">Proposed split</h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Expand a tier row to see how accrual adjustments and current-period
                      allocations build the subtotal.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {viewMode === 'table' && (
                      <button
                        type="button"
                        onClick={toggleAllTierBuildups}
                        className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {allTiersExpanded ? 'Collapse all' : 'Expand all'}
                      </button>
                    )}
                    <div className="flex rounded-md border border-gray-200 text-xs overflow-hidden">
                    {(['table', 'flow'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setViewMode(v)}
                        className={`px-3 py-1.5 capitalize ${
                          viewMode === v ? 'bg-brand-50 text-brand-800 font-medium' : 'text-gray-600'
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                    </div>
                  </div>
                </div>

                {viewMode === 'table' && splitTableDisplayWidths && (
                  <div className="table-scroll-mobile overflow-x-auto overscroll-x-contain py-4 md:px-4">
                    <table
                      className="w-full table-fixed text-sm border-separate border-spacing-0"
                      style={{ minWidth: splitTableMinW }}
                    >
                      <SplitTableColgroup widths={splitTableDisplayWidths} />
                      <thead className="bg-gray-50">
                        <tr>
                          <th className={SPLIT_LABEL_HEAD}>Tier</th>
                          {MEMBERS.map((m) => (
                            <th key={m} className={SPLIT_NUM_HEAD}>
                              {m}
                            </th>
                          ))}
                          <th className={SPLIT_TOTAL_HEAD}>Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {tierKeys().map((tierKey) => {
                          const tier = proposalGrid[tierKey]
                          const total = tierColumnTotal(tier)
                          if (total === 0 && tierKey.startsWith('s4')) return null
                          const expanded = expandedTiers.has(tierKey)
                          return (
                            <Fragment key={tierKey}>
                              {expanded && snapshot && (
                                <TierBuildupDetailRows
                                  tierKey={tierKey}
                                  result={snapshot.result}
                                  proposalGrid={proposalGrid}
                                  labelCellClass={SPLIT_LABEL_CELL}
                                  numCellClass={SPLIT_NUM_CELL}
                                  totalCellClass={SPLIT_TOTAL_CELL}
                                />
                              )}
                              <tr
                                className={`group ${expanded ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                              >
                                <td
                                  className={`${SPLIT_LABEL_CELL} text-gray-700 ${
                                    expanded ? 'bg-gray-50 font-medium' : 'group-hover:bg-gray-50'
                                  }`}
                                >
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedTiers((prev) => {
                                        const next = new Set(prev)
                                        if (next.has(tierKey)) next.delete(tierKey)
                                        else next.add(tierKey)
                                        return next
                                      })
                                    }
                                    className="inline-flex items-center gap-1.5 text-left hover:text-brand-700"
                                    aria-expanded={expanded}
                                  >
                                    <span className="text-gray-400 w-3">{expanded ? '▾' : '▸'}</span>
                                    {TIER_LABELS[tierKey]}
                                  </button>
                                </td>
                                {MEMBERS.map((m) => {
                                  const withheld =
                                    tierKey === 's3b'
                                      ? snapshot.result.proposal.withheld.s3b[m]
                                      : tierKey === 's4b'
                                        ? snapshot.result.proposal.withheld.s4b[m]
                                        : 0
                                  return (
                                    <td
                                      key={m}
                                      className={`${SPLIT_NUM_CELL} text-gray-800 ${
                                        expanded ? 'bg-gray-50 font-medium' : ''
                                      }`}
                                    >
                                      {tier[m] === 0 ? (
                                        '—'
                                      ) : (
                                        <span className="inline-flex flex-wrap items-center justify-end gap-1">
                                          {fmt.format(tier[m])}
                                          {withheld > 0.005 && (
                                            <HoverHelp
                                              label="withheld"
                                              aria-label="Withheld from cash"
                                            >
                                              <p>
                                                Scott&apos;s vested share of this pool. Declared to
                                                his Tier 3 Distributions account and accrued to
                                                Distributions Payable — not paid in cash. Pay it from
                                                the Vesting catch-up panel.
                                              </p>
                                            </HoverHelp>
                                          )}
                                        </span>
                                      )}
                                    </td>
                                  )
                                })}
                                <td
                                  className={`${SPLIT_TOTAL_CELL} ${
                                    expanded ? 'bg-gray-50 font-medium' : ''
                                  }`}
                                >
                                  {fmt.format(total)}
                                </td>
                              </tr>
                              {expanded && snapshot && (
                                <TierBuildupRemainingRow
                                  tierKey={tierKey}
                                  result={snapshot.result}
                                  proposalGrid={proposalGrid}
                                  labelCellClass={SPLIT_LABEL_CELL}
                                  numCellClass={SPLIT_NUM_CELL}
                                  totalCellClass={SPLIT_TOTAL_CELL}
                                />
                              )}
                            </Fragment>
                          )
                        })}
                        <tr aria-label="Calculated proposal total">
                          <td className={SPLIT_TOTALS_LABEL_CELL}>Calculated total</td>
                          {MEMBERS.map((m) => (
                            <td key={m} className={SPLIT_TOTALS_NUM_CELL}>
                              {fmt.format(snapshot.result.proposal.by_member[m])}
                            </td>
                          ))}
                          <td className={SPLIT_TOTALS_TOTAL_CELL}>
                            {fmt.format(snapshot.result.proposal.total)}
                          </td>
                        </tr>
                        <tr aria-label="Cash paid to members">
                          <td className={SPLIT_TOTALS_LABEL_CELL}>Cash paid</td>
                          {MEMBERS.map((m) => (
                            <td key={m} className={SPLIT_TOTALS_NUM_CELL}>
                              {fmt.format(snapshot.result.proposal.cash_by_member[m])}
                            </td>
                          ))}
                          <td className={SPLIT_TOTALS_TOTAL_CELL}>
                            {fmt.format(snapshot.result.proposal.cash_total)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {snapshot.result.proposal.withheld_total > 0.005 && (
                      <p className="mt-3 text-xs text-gray-600">
                        {fmt.format(snapshot.result.proposal.withheld_total)} of Scott&apos;s vested
                        share is withheld and accrued to Distributions Payable.
                      </p>
                    )}
                    {applyTrueUps &&
                      obligations &&
                      obligations.accruals_t1_hp_outstanding > 0.005 && (
                        <p className="mt-3 text-xs text-gray-600">
                          This proposal includes{' '}
                          <span className="font-medium tabular-nums text-gray-800">
                            {fmt.format(obligations.accruals_t1_hp_outstanding)}
                          </span>{' '}
                          of Tier 1+2 accrual catch-ups from the Accruals tab (expand a tier for
                          the breakdown). Scott&apos;s vesting balance is handled in the catch-up
                          panel below.
                        </p>
                      )}
                  </div>
                )}

                {viewMode === 'flow' && (
                  <div className="p-4">
                    <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
                      <WaterfallFlow result={result} compact />
                      <WaterfallSankey result={result} embedded />
                    </div>
                  </div>
                )}
              </section>

              {snapshot.vesting.length > 0 && (
                <VestingCatchUpPanel
                  items={snapshot.vesting}
                  catchUpAmounts={catchUpAmounts}
                  onCatchUpChange={handleCatchUpChange}
                  catchUpSeparate={catchUpSeparate}
                  onCatchUpSeparateChange={setCatchUpSeparate}
                  splitTableWidths={splitTableDisplayWidths}
                  splitTableMinWidth={splitTableMinW ?? 0}
                  disabled={logging || resultStale}
                />
              )}

              <section className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900">Actual payment</h3>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {canEdit
                      ? 'Adjust who gets paid today, then log to BookKeeper history.'
                      : 'Adjust who gets paid today (read-only — logging is disabled).'}
                  </p>
                  {distributionCatchUpTotal > 0 && (
                    <p className="mt-1 text-xs text-amber-800">
                      {fmt.format(distributionCatchUpTotal)} vesting catch-up is included in the
                      Actual payment row above.
                    </p>
                  )}
                </div>
                <div className="table-scroll-mobile overflow-x-auto overscroll-x-contain py-4 md:px-4">
                  {splitTableDisplayWidths && (
                  <table
                    className="w-full table-fixed text-sm border-separate border-spacing-0"
                    style={{ minWidth: splitTableMinW }}
                  >
                    <SplitTableColgroup widths={splitTableDisplayWidths} />
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={SPLIT_LABEL_HEAD} />
                        {MEMBERS.map((m) => (
                          <th key={m} className={SPLIT_NUM_HEAD}>
                            {m}
                          </th>
                        ))}
                        <th className={SPLIT_TOTAL_HEAD}>Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr className="bg-brand-50/50">
                        <td className={`${SPLIT_LABEL_CELL} bg-brand-50 font-medium text-brand-900`}>
                          Actual payment
                        </td>
                        {MEMBERS.map((m) => {
                          const delta = paymentDeltas?.[m] ?? 0
                          const displayed = displayActualAmount(m)
                          const currentAmount =
                            editingMember === m
                              ? (parseDistributionAmount(memberDraft) ?? 0)
                              : displayed
                          const showCopyIcon = currentAmount > 0.005
                          const isLocked = lockedMembers.has(m)
                          const withheldRemainder = R2(smVN - smCatchUpSplit.c1)
                          return (
                            <td key={m} className={`${SPLIT_NUM_CELL} bg-brand-50/50 px-2 py-1`}>
                              {isLocked ? (
                                <div className="text-right">
                                  <span className="tabular-nums font-medium text-gray-900">
                                    {fmt.format(displayed)}
                                  </span>
                                  {!catchUpSeparate && withheldRemainder > 0.005 && (
                                    <p className="text-xs text-gray-500">
                                      withheld {fmt.format(withheldRemainder)}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="relative">
                                  {showCopyIcon && (
                                    <button
                                      type="button"
                                      onClick={() => void copyActualPayment(m)}
                                      onMouseDown={(e) => e.preventDefault()}
                                      className="absolute left-1 top-1/2 z-10 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                                      title="Copy amount"
                                      aria-label={`Copy actual payment for ${m}`}
                                    >
                                      <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={
                                      editingMember === m
                                        ? memberDraft
                                        : formatCurrencyField(displayed)
                                    }
                                    placeholder="$0.00"
                                    onFocus={() => {
                                      setEditingMember(m)
                                      setMemberDraft(
                                        displayed === 0 ? '' : formatCurrencyField(displayed),
                                      )
                                    }}
                                    onChange={(e) => setMemberDraft(e.target.value)}
                                    onBlur={() => {
                                      updateActualTotal(m, memberDraft)
                                      setEditingMember(null)
                                    }}
                                    className={`w-full rounded border py-1 text-right text-sm tabular-nums ${
                                      showCopyIcon ? 'pl-7 pr-2' : 'px-2'
                                    } ${
                                      Math.abs(delta) > 0.005
                                        ? 'border-amber-400 bg-amber-50'
                                        : 'border-gray-200 bg-white'
                                    }`}
                                  />
                                </div>
                              )}
                            </td>
                          )
                        })}
                        <td className={`${SPLIT_TOTAL_CELL} bg-brand-50/50`}>
                          {fmt.format(MEMBERS.reduce((sum, m) => sum + displayActualAmount(m), 0))}
                        </td>
                      </tr>
                      {hasStandaloneCatchUpRow && (
                        <tr className="bg-brand-50/40">
                          <td
                            className={`${SPLIT_LABEL_CELL} bg-brand-50 font-medium text-brand-900`}
                          >
                            Catch-up payment
                          </td>
                          {MEMBERS.map((m) => {
                            const item = snapshot.vesting.find((v) => v.member === m)
                            const canEdit =
                              catchUpSeparate &&
                              catchUpAmounts[m] > 0.005 &&
                              item &&
                              item.pro_forma_payable > 0.005 &&
                              !logging &&
                              !resultStale
                            return (
                              <td key={m} className={`${SPLIT_NUM_CELL} bg-brand-50/40 px-2 py-1`}>
                                {canEdit ? (
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={
                                      editingCatchUpMember === m
                                        ? catchUpMemberDraft
                                        : formatCurrencyField(catchUpAmounts[m])
                                    }
                                    placeholder="$0.00"
                                    onFocus={() => {
                                      setEditingCatchUpMember(m)
                                      setCatchUpMemberDraft(
                                        catchUpAmounts[m] === 0
                                          ? ''
                                          : formatCurrencyField(catchUpAmounts[m]),
                                      )
                                    }}
                                    onChange={(e) => setCatchUpMemberDraft(e.target.value)}
                                    onBlur={() => {
                                      updateStandaloneCatchUp(m, catchUpMemberDraft)
                                      setEditingCatchUpMember(null)
                                    }}
                                    className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-right text-sm tabular-nums"
                                  />
                                ) : catchUpSeparate && catchUpAmounts[m] > 0.005 ? (
                                  <span className="text-gray-700">{fmt.format(catchUpAmounts[m])}</span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                            )
                          })}
                          <td className={`${SPLIT_TOTAL_CELL} bg-brand-50/40`}>
                            {fmt.format(standaloneCatchUpTotal)}
                          </td>
                        </tr>
                      )}
                      <tr className="group hover:bg-gray-50">
                        <td className={`${SPLIT_LABEL_CELL} text-gray-600 group-hover:bg-gray-50`}>
                          Difference
                        </td>
                        {MEMBERS.map((m) => {
                          const delta = paymentDeltas?.[m] ?? 0
                          return (
                            <td
                              key={m}
                              className={`${SPLIT_NUM_CELL} text-xs ${
                                Math.abs(delta) > 0.005 ? 'text-amber-800' : 'text-gray-400'
                              }`}
                            >
                              {Math.abs(delta) > 0.005
                                ? delta < 0
                                  ? `${fmt.format(Math.abs(delta))} owed`
                                  : `${fmt.format(delta)} over`
                                : '—'}
                            </td>
                          )
                        })}
                        <td
                          className={`${SPLIT_TOTAL_CELL} text-xs ${
                            Math.abs(
                              MEMBERS.reduce((sum, m) => sum + actualTotals[m], 0) -
                                snapshot.result.proposal.total,
                            ) > 0.005
                              ? 'text-amber-800'
                              : 'text-gray-400'
                          }`}
                        >
                          {Math.abs(
                            MEMBERS.reduce((sum, m) => sum + actualTotals[m], 0) -
                              snapshot.result.proposal.total,
                          ) > 0.005
                            ? fmt.format(
                                MEMBERS.reduce((sum, m) => sum + actualTotals[m], 0) -
                                  snapshot.result.proposal.total,
                              )
                            : '—'}
                        </td>
                      </tr>
                      {logGrid && proposalWithheld && (
                        <>
                          <tr className={TOTALS_ROW_BG}>
                            <td className={SPLIT_TOTALS_LABEL_CELL}>Gross</td>
                            {MEMBERS.map((m) => {
                              const memberGross =
                                memberTotalsFromGrid(logGrid)[m] +
                                (!catchUpSeparate && m === 'SM' ? smCatchUpSplit.c2 : 0)
                              return (
                                <td key={m} className={SPLIT_TOTALS_NUM_CELL}>
                                  {fmt.format(memberGross)}
                                </td>
                              )
                            })}
                            <td className={SPLIT_TOTALS_TOTAL_CELL}>
                              {fmt.format(grossPaymentTotal)}
                            </td>
                          </tr>
                          <tr className={TOTALS_ROW_BG}>
                            <td className={SPLIT_TOTALS_LABEL_CELL}>Cash</td>
                            {MEMBERS.map((m) => (
                              <td key={m} className={SPLIT_TOTALS_NUM_CELL}>
                                {pendingLogFields
                                  ? fmt.format(
                                      memberTotalsFromGrid(logGrid)[m] -
                                        memberWithheld(pendingLogFields.withheld, m),
                                    )
                                  : '—'}
                              </td>
                            ))}
                            <td className={SPLIT_TOTALS_TOTAL_CELL}>
                              {fmt.format(cashPaymentTotal)}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                  )}
                </div>
              </section>
              </div>
              {canEdit && (
                <section className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                  <div className="space-y-3 px-4 py-4">
                    <p className="text-xs text-gray-500">
                      Provide actual payments made in this distribution for each member. Then click
                      the button below to log actual payments in the History tab.
                    </p>
                    {catchUpSeparate && standaloneCatchUpTotal > 0.005 && (
                      <p className="text-xs text-gray-500">
                        Vesting catch-up payment will be logged separately from the ordinary
                        distribution.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setConfirmLogOpen(true)}
                      disabled={!canLog}
                      className="inline-flex items-center rounded-md px-5 py-2.5 text-sm font-semibold text-white shadow-md disabled:cursor-not-allowed disabled:opacity-50 btn-primary"
                      style={{ background: 'var(--gradient-primary)' }}
                    >
                      Log Payments
                    </button>
                  </div>
                </section>
              )}
            </div>
          )}
      </div>

      {canEdit && confirmLogOpen && snapshot && actualTotals && logGrid && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--overlay-standard)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900">Log distribution?</h3>
            <p className="mt-2 text-sm text-gray-600">
              Distribution #{Number(snapshot.result.inputs.num)} on{' '}
              <strong>{snapshot.inputs.distDate}</strong>
            </p>
            <ul className="mt-2 text-sm text-gray-700 space-y-1">
              <li>
                Gross: <strong>{fmt.format(grossPaymentTotal)}</strong>
              </li>
              <li>
                Cash paid to members: <strong>{fmt.format(cashPaymentTotal)}</strong>
              </li>
              {pendingLogFields &&
                MEMBERS.reduce(
                  (s, m) => s + memberWithheld(pendingLogFields.withheld, m),
                  0,
                ) > 0.005 && (
                  <li>
                    Withheld (accrued to Scott&apos;s payable):{' '}
                    <strong>
                      {fmt.format(
                        MEMBERS.reduce(
                          (s, m) => s + memberWithheld(pendingLogFields.withheld, m),
                          0,
                        ),
                      )}
                    </strong>
                  </li>
                )}
              {catchUpAmounts.SM > 0.005 && (
                <li>
                  Vesting catch-up: <strong>{fmt.format(catchUpAmounts.SM)}</strong>
                  {catchUpSeparate ? ' (separate distribution)' : ' (this distribution)'}
                </li>
              )}
            </ul>
            {standaloneCatchUpTotal > 0 && catchUpSeparate && (
              <p className="mt-2 text-xs text-gray-600">
                Separate catch-up totaling{' '}
                <strong>{fmt.format(standaloneCatchUpTotal)}</strong> will also be logged.
              </p>
            )}
            {standaloneCatchUpTotal > 0 && (
              <ul className="mt-2 text-xs text-gray-600 list-disc list-inside">
                {MEMBERS.filter((m) => catchUpAmounts[m] > 0.005).map((m) => (
                  <li key={m}>
                    Separate catch-up — {m}: {fmt.format(catchUpAmounts[m])}
                  </li>
                ))}
              </ul>
            )}
            {distributionCatchUpTotal > 0 && (
              <ul className="mt-2 text-xs text-gray-600 list-disc list-inside">
                {MEMBERS.filter((m) => catchUpAmounts[m] > 0.005 && !catchUpSeparate).map((m) => (
                  <li key={m}>
                    {m} catch-up: {fmt.format(catchUpAmounts[m])}
                  </li>
                ))}
              </ul>
            )}
            <label className="mt-4 block text-sm">
              <span className="font-medium text-gray-700">Note (optional)</span>
              <input
                type="text"
                value={logNote}
                onChange={(e) => setLogNote(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="e.g. ACH cleared 7/11"
              />
            </label>
            <div className="mt-4 overflow-x-auto rounded border border-gray-200">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left">Member</th>
                    <th className="px-2 py-1 text-right">To log</th>
                    <th className="px-2 py-1 text-right">Δ vs calculated</th>
                  </tr>
                </thead>
                <tbody>
                  {MEMBERS.map((member) => {
                    const logged = displayActualAmount(member)
                    const proposed = snapshot.result.proposal.by_member[member]
                    const delta = logged - proposed
                    return (
                      <tr key={member}>
                        <td className="px-2 py-1">{member}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt.format(logged)}</td>
                        <td
                          className={`px-2 py-1 text-right tabular-nums ${
                            Math.abs(delta) > 0.005 ? 'text-amber-700' : 'text-gray-400'
                          }`}
                        >
                          {Math.abs(delta) > 0.005 ? fmt.format(delta) : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmLogOpen(false)}
                disabled={logging}
                className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleLogDistribution()}
                disabled={logging}
                className="rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 btn-primary"
                style={{ background: 'var(--gradient-primary)' }}
              >
                {logging ? 'Logging…' : 'Yes, log it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Calculator() {
  const mode = useDistributionModeStore((s) => s.mode)
  return (
    <div className="flex flex-col items-start">
      <DistributionModeToggle className="mb-6" />
      <div className="w-full">
        {mode === 'income' ? <AllocationCalculator /> : <CalculatorCash />}
      </div>
    </div>
  )
}
